const RECAPTCHA_SCRIPT_ID = 'g2g-admin-recaptcha-v3'
const RECAPTCHA_BASE_URL = 'https://www.google.com/recaptcha/api.js'
const RECAPTCHA_READY_TIMEOUT = 10000
const RECAPTCHA_POLL_INTERVAL = 100

let recaptchaScriptPromise

const createRecaptchaError = (message) => {
  const error = new Error(message)
  error.name = 'RecaptchaError'
  return error
}

const waitForRecaptcha = () => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now()

    const checkRecaptcha = () => {
      if (
        window.grecaptcha &&
        typeof window.grecaptcha.ready === 'function'
      ) {
        window.grecaptcha.ready(() => {
          if (typeof window.grecaptcha.execute === 'function') {
            resolve(window.grecaptcha)
            return
          }

          if (Date.now() - startTime >= RECAPTCHA_READY_TIMEOUT) {
            reject(createRecaptchaError('Unable to initialize reCAPTCHA.'))
            return
          }

          window.setTimeout(checkRecaptcha, RECAPTCHA_POLL_INTERVAL)
        })
        return
      }

      if (Date.now() - startTime >= RECAPTCHA_READY_TIMEOUT) {
        reject(createRecaptchaError('Unable to initialize reCAPTCHA.'))
        return
      }

      window.setTimeout(checkRecaptcha, RECAPTCHA_POLL_INTERVAL)
    }

    checkRecaptcha()
  })
}

const attachRecaptchaListeners = (script, resolve, reject) => {
  const onLoad = () => {
    script.removeEventListener('load', onLoad)
    script.removeEventListener('error', onError)

    waitForRecaptcha()
      .then(resolve)
      .catch((error) => {
        recaptchaScriptPromise = null
        reject(error)
      })
  }

  const onError = () => {
    script.removeEventListener('load', onLoad)
    script.removeEventListener('error', onError)
    recaptchaScriptPromise = null
    reject(createRecaptchaError('Unable to load reCAPTCHA. Please refresh the page.'))
  }

  script.addEventListener('load', onLoad)
  script.addEventListener('error', onError)
}

export const loadRecaptcha = (siteKey) => {
  if (!siteKey) {
    return Promise.reject(
      createRecaptchaError('Security check is not configured for this environment.')
    )
  }

  if (window.grecaptcha && typeof window.grecaptcha.execute === 'function') {
    return waitForRecaptcha()
  }

  if (!recaptchaScriptPromise) {
    recaptchaScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID)

      if (existingScript) {
        waitForRecaptcha()
          .then(resolve)
          .catch((error) => {
            recaptchaScriptPromise = null
            reject(error)
          })
        attachRecaptchaListeners(existingScript, resolve, reject)
        return
      }

      const script = document.createElement('script')
      script.id = RECAPTCHA_SCRIPT_ID
      script.src = `${RECAPTCHA_BASE_URL}?render=${siteKey}`
      script.async = true
      script.defer = true

      attachRecaptchaListeners(script, resolve, reject)
      document.body.appendChild(script)
    })
  }

  return recaptchaScriptPromise
}

export const executeRecaptcha = async (siteKey, action) => {
  const grecaptcha = await loadRecaptcha(siteKey)
  const token = await grecaptcha.execute(siteKey, { action })

  if (!token) {
    throw createRecaptchaError('Unable to validate reCAPTCHA. Please try again.')
  }

  return token
}
